import { XMLParser } from 'fast-xml-parser';
import { v4 as uuidv4 } from 'uuid';
import {
  AmortizationType,
  LienPriorityType,
  LoanPurpose,
  MortgageType,
  ReceivedFrom,
  UsageType,
  StringBoolean,
} from 'constants/enums';
import { capitalizeString } from './index';
import { pascalCaseToEnumValue } from './enumUtils';
import { getNowAsUtcIso } from './dateUtils';
import loanOriginators from '../loanOriginators';

// ---------------------------------------------------------------------------
// Parser config
// ---------------------------------------------------------------------------
// preserveOrder is NOT needed. fast-xml-parser collapses repeated sibling
// elements into arrays automatically when isArray returns true for that tag.
// ---------------------------------------------------------------------------
const ARRAY_TAGS = new Set([
  'PARTY',
  'ROLE',
  'LIABILITY',
  'ASSET',
  'HOUSING_EXPENSE',
  'CONTACT_POINT',
  'LICENSE',
  'RELATIONSHIP',
  'EMPLOYER',
  'RESIDENCE',
  'CURRENT_INCOME_ITEM',
  'PROPERTY_VALUATION',
  'LOAN_IDENTIFIER',
]);

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '_',
  trimValues: true,
  // Keep amounts as strings to avoid floating point / precision issues
  parseTagValue: true,
  numberParseOptions: {
    leadingZeros: false,
    skipLike: /^\d+\.\d+$/, // skip anything that looks like a decimal amount
  },
  isArray: (name) => ARRAY_TAGS.has(name),
});

// ---------------------------------------------------------------------------
// Root navigation
// ---------------------------------------------------------------------------
const getDeal = (parsed) => parsed?.MESSAGE?.DEAL_SETS?.DEAL_SET?.DEALS?.DEAL;

// ---------------------------------------------------------------------------
// Name helper
// DU exports NAME as a bare <n> tag (outside the default namespace).
// Calyx / other LOS tools use <NAME>. We handle both.
// ---------------------------------------------------------------------------
const getName = (individual) => individual?.NAME ?? individual?.n ?? {};

// ---------------------------------------------------------------------------
// Contact point helpers
// ---------------------------------------------------------------------------
const getPhone = (contactPoints = []) => {
  if (!contactPoints.length) return null;

  // Prefer mobile; fall back to first telephone found
  const mobile = contactPoints.find(
    (cp) => cp.CONTACT_POINT_TELEPHONE && cp.CONTACT_POINT_DETAIL?.ContactPointRoleType === 'Mobile'
  );
  const any = contactPoints.find((cp) => cp.CONTACT_POINT_TELEPHONE);
  const chosen = mobile ?? any;
  return chosen?.CONTACT_POINT_TELEPHONE?.ContactPointTelephoneValue ?? null;
};

const getEmail = (contactPoints = []) =>
  contactPoints.find((cp) => cp.CONTACT_POINT_EMAIL)?.CONTACT_POINT_EMAIL?.ContactPointEmailValue ?? null;

// ---------------------------------------------------------------------------
// Collaterals
// ---------------------------------------------------------------------------
const extractCollaterals = (parsed) => {
  const subjectProp = getDeal(parsed)?.COLLATERALS?.COLLATERAL?.SUBJECT_PROPERTY;
  if (!subjectProp) throw new Error('No Subject Property in file');

  const { ADDRESS, PROPERTY_DETAIL, PROPERTY_VALUATIONS } = subjectProp;
  if (!ADDRESS) throw new Error('No Address in Subject Property');
  if (!PROPERTY_DETAIL) throw new Error('No Property Detail in Subject Property');

  const rawValuation =
    PROPERTY_VALUATIONS?.PROPERTY_VALUATION?.[0]?.PROPERTY_VALUATION_DETAIL?.PropertyValuationAmount ??
    PROPERTY_DETAIL?.PropertyEstimatedValueAmount ??
    null;
  const subjectPropValuation = rawValuation != null ? parseFloat(rawValuation) : null;

  return {
    subjectPropAddress: ADDRESS.AddressLineText,
    subjectPropCity: ADDRESS.CityName,
    subjectPropCounty: ADDRESS.CountyName,
    subjectPropZip: ADDRESS.PostalCode,
    subjectPropState: ADDRESS.StateCode,
    financedUnitCount: PROPERTY_DETAIL.FinancedUnitCount,
    usageType: pascalCaseToEnumValue(PROPERTY_DETAIL.PropertyUsageType, UsageType),
    subjectPropValuation,
  };
};

// ---------------------------------------------------------------------------
// Loan
// ---------------------------------------------------------------------------
const extractLoan = (parsed) => {
  const loans = getDeal(parsed)?.LOANS;
  if (!loans) throw new Error('No Loans in file');

  // Take the first loan marked SubjectLoan, or just the first loan
  const loanList = Array.isArray(loans.LOAN) ? loans.LOAN : [loans.LOAN];
  const loan =
    // eslint-disable-next-line no-underscore-dangle
    loanList.find((l) => l._LoanRoleType === 'SubjectLoan') ?? loanList[0];
  if (!loan) throw new Error('No Loan in file');

  // Amortization
  const amortizationRule = loan.AMORTIZATION?.AMORTIZATION_RULE;
  const amortizationType = amortizationRule?.AmortizationType;
  const amortizationPeriod = amortizationRule?.LoanAmortizationPeriodCount;

  // Term — prefer MATURITY block, fall back to amortization period
  const term = loan.MATURITY?.MATURITY_RULE?.LoanMaturityPeriodCount ?? amortizationPeriod;

  // HOEPA — Boolean in schema, default null if not present
  const isHoepaRaw = loan.HMDA_LOAN?.HMDA_LOAN_DETAIL?.HMDA_HOEPALoanStatusIndicator;
  const isHoepa = isHoepaRaw != null ? isHoepaRaw === true || isHoepaRaw === 'true' : null;

  // Terms of loan
  const termsOfLoan = loan.TERMS_OF_LOAN;
  if (!termsOfLoan) throw new Error('No Terms of Loan in file');

  const lienPriorityType = termsOfLoan.LienPriorityType;
  const loanPurposeRaw = termsOfLoan.LoanPurposeType;
  const mortgageType = termsOfLoan.MortgageType;
  const loanAmount = termsOfLoan.NoteAmount;
  const interestRate = termsOfLoan.NoteRatePercent;

  // Refinance sub-type
  let loanPurposeSubType = null;
  if (loanPurposeRaw === 'Refinance') {
    loanPurposeSubType = loan.REFINANCE?.RefinanceCashOutDeterminationType ?? null;
  }

  // Application date
  const appDate = loan.LOAN_DETAIL?.ApplicationReceivedDate ?? null;

  return {
    applicationDate: appDate || null,
    amortizationType: pascalCaseToEnumValue(amortizationType, AmortizationType),
    amortizationPeriod: amortizationPeriod != null ? parseInt(amortizationPeriod, 10) : null,
    isHoepa,
    term: term != null ? String(term) : null, // schema: String
    lienPriorityType: pascalCaseToEnumValue(lienPriorityType, LienPriorityType),
    loanPurpose: loanPurposeSubType
      ? pascalCaseToEnumValue(`${loanPurposeSubType}Refinance`, LoanPurpose)
      : pascalCaseToEnumValue(loanPurposeRaw, LoanPurpose),
    mortgageType: pascalCaseToEnumValue(mortgageType, MortgageType),
    loanAmount: loanAmount != null ? parseFloat(loanAmount) : null, // schema: Float
    interestRate: interestRate != null ? parseFloat(interestRate) : null, // schema: Float!
  };
};

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------
const extractBorrowerData = (party) => {
  const individual = party.INDIVIDUAL;
  if (!individual) return null;

  const name = getName(individual);
  const firstName = name.FirstName;
  const lastName = name.LastName;

  const contactPoints = individual.CONTACT_POINTS?.CONTACT_POINT ?? [];
  const email = getEmail(contactPoints);
  const phone = getPhone(contactPoints);

  // BORROWER is nested under ROLES > ROLE[]
  const borrowerRole = party.ROLES?.ROLE?.find((r) => r.BORROWER);
  const borrower = borrowerRole?.BORROWER;
  const dob = borrower?.BORROWER_DETAIL?.BorrowerBirthDate ?? null;

  // Current residence address
  const currentResidence = borrower?.RESIDENCES?.RESIDENCE?.find(
    (r) => r.RESIDENCE_DETAIL?.BorrowerResidencyType === 'Current'
  );
  const address = currentResidence?.ADDRESS ?? {};

  return {
    firstName: capitalizeString(firstName),
    lastName: capitalizeString(lastName),
    email,
    phone: phone != null ? String(phone) : null,
    dob,
    address: address.AddressLineText ?? null,
    city: address.CityName ?? null,
    country: address.CountryCode ?? null,
    state: address.StateCode ?? null,
    zip: address.PostalCode ?? null,
  };
};

const extractParties = (parsed) => {
  const parties = getDeal(parsed)?.PARTIES?.PARTY;
  if (!parties?.length) throw new Error('No Parties in file');

  // Split by role
  const getBorrowerRole = (p) => p.ROLES?.ROLE?.find((r) => r.ROLE_DETAIL?.PartyRoleType === 'Borrower');
  const getOriginatorRole = (p) => p.ROLES?.ROLE?.find((r) => r.ROLE_DETAIL?.PartyRoleType === 'LoanOriginator');

  const borrowerParties = parties.filter(getBorrowerRole);
  const originatorParty = parties.find(getOriginatorRole);

  if (!borrowerParties.length) throw new Error('No Borrower party in file');

  // Primary borrower
  const primary = extractBorrowerData(borrowerParties[0]);

  // Co-borrower — only include if same address as primary
  let coBorrower = {
    coBorrowerFirstName: null,
    coBorrowerLastName: null,
    coBorrowerEmail: null,
    coBorrowerPhone: null,
    coBorrowerDob: null,
  };

  if (borrowerParties.length > 1) {
    const co = extractBorrowerData(borrowerParties[1]);
    if (
      co &&
      co.address === primary.address &&
      co.city === primary.city &&
      co.state === primary.state &&
      co.zip === primary.zip &&
      co.country === primary.country
    ) {
      coBorrower = {
        coBorrowerFirstName: co.firstName,
        coBorrowerLastName: co.lastName,
        coBorrowerEmail: co.email,
        coBorrowerPhone: co.phone,
        coBorrowerDob: co.dob,
      };
    }
  }

  // Loan originator name
  let loanOriginatorName = null;
  if (originatorParty) {
    const individual = originatorParty.INDIVIDUAL;
    if (individual) {
      const name = getName(individual);
      loanOriginatorName = capitalizeString(name.FullName ?? null);
    }
  }

  return {
    borrowerFirstName: primary.firstName,
    borrowerLastName: primary.lastName,
    borrowerDob: primary.dob,
    borrowerEmail: primary.email,
    borrowerPhone: primary.phone,
    borrowerAddress: primary.address,
    borrowerCity: primary.city,
    borrowerCountry: primary.country,
    borrowerState: primary.state,
    borrowerZip: primary.zip,
    ...coBorrower,
    loanOriginatorName: loanOriginators.find((it) => it.label === loanOriginatorName || it.alias === loanOriginatorName)
      ?.value,
  };
};

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
export const parseFile = (xml, tenantId) => {
  try {
    const parsed = parser.parse(xml);
    const collaterals = extractCollaterals(parsed);
    const loan = extractLoan(parsed);
    const parties = extractParties(parsed);
    const fileData = {
      id: uuidv4(),
      tenantId,
      importDate: getNowAsUtcIso(),
      isDeleted: StringBoolean.False.value,
      receivedFrom: ReceivedFrom.Borrower.value,
      ...collaterals,
      ...loan,
      ...parties,
    };
    console.log('MISMO parsed file:', fileData);
    return fileData;
  } catch (error) {
    console.error('MISMO parse error:', error);
    return null;
  }
};
